import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Hotkey } from 'config/enums';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { CallDetailsStatus } from 'features/call/call-details/enums';
import { setCallDetailsStatus } from 'features/call/call-details/store';
import { handleError } from 'features/notification/store';
import { useSpawnNewCall } from 'features/spawn-call/hooks';
import { shouldCallControlsBeVisibleSelector } from 'features/spawn-call/store';
import { Button } from 'features/ui/elements';

interface CallDetailsButtonNewCallProps {
  isLoading?: boolean;
}

export const CallDetailsButtonNewCall: FC<CallDetailsButtonNewCallProps> = ({
  isLoading,
}) => {
  const dispatch = useAppDispatch();
  const [isSpawnCallInProgress, setIsSpawnCallInProgress] = useState(false);
  const { t } = useTranslation('translation', { keyPrefix: 'newCall' });
  const { spawnNewCall } = useSpawnNewCall();
  const isSpawnCallButtonVisible = useAppSelector(
    shouldCallControlsBeVisibleSelector
  );

  const handleNewCall = async () => {
    setIsSpawnCallInProgress(true);
    try {
      await spawnNewCall();
      dispatch(setCallDetailsStatus(CallDetailsStatus.NEW_CALL));
    } catch (error) {
      dispatch(
        handleError({
          message: t('requestErrorMessage'),
          methodName: 'handleNewCall',
          error,
        })
      );
    } finally {
      setIsSpawnCallInProgress(false);
    }
  };

  if (!isSpawnCallButtonVisible) {
    return;
  }

  return (
    <Button
      fullWidth
      size='small'
      color='secondary'
      data-testid='spawn-call-button'
      loading={isSpawnCallInProgress || isLoading}
      onClick={handleNewCall}
      hotkey={Hotkey.NewCall}
    >
      {t('openNewCallBtn')}
    </Button>
  );
};
